import React, { useState, useEffect } from 'react';

export default function Profile({ session, profile, onSave }) {
  const [form, setForm] = useState({
    company_name:  '',
    cage_code:     '',
    contact_name:  '',
    phone:         '',
    address:       '',
    city:          '',
    state:         '',
    zip:           '',
    employees:     '',
    website:       '',
  });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (profile) {
      setForm({
        company_name:  profile.company_name  || '',
        cage_code:     profile.cage_code     || '',
        contact_name:  profile.contact_name  || '',
        phone:         profile.phone         || '',
        address:       profile.address       || '',
        city:          profile.city          || '',
        state:         profile.state         || '',
        zip:           profile.zip           || '',
        employees:     profile.employees     || '',
        website:       profile.website       || '',
      });
    }
  }, [profile]);

  function handleChange(key, value) {
    setForm(prev => ({ ...prev, [key]: value }));
    setSaved(false);
  }

  async function handleSave() {
    await onSave(form);
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  }

  const inputStyle = {
    width: '100%', background: 'var(--surface)',
    border: '1px solid var(--border2)', borderRadius: 6,
    padding: '9px 12px', fontSize: 13, color: 'var(--text)',
    fontFamily: 'var(--font)', outline: 'none', transition: 'border-color 0.15s',
  };

  const labelStyle = {
    fontSize: 11, color: 'var(--text3)', marginBottom: 5,
    display: 'block', textTransform: 'uppercase', letterSpacing: '0.06em',
  };

  const sections = [
    {
      title: 'Company Information',
      fields: [
        { label: 'Company Name',          key: 'company_name',  placeholder: 'e.g. Acme Defense Co.',        type: 'text',  full: true  },
        { label: 'CAGE Code',             key: 'cage_code',     placeholder: 'e.g. 7ACB4',                   type: 'text',  full: false },
        { label: 'Number of Employees',   key: 'employees',     placeholder: 'e.g. 45',                      type: 'text',  full: false },
        { label: 'Website',               key: 'website',       placeholder: 'e.g. https://acmedefense.com', type: 'text',  full: true  },
      ],
    },
    {
      title: 'Primary Contact',
      fields: [
        { label: 'Contact Name',  key: 'contact_name', placeholder: 'e.g. John Smith',        type: 'text',  full: false },
        { label: 'Phone Number',  key: 'phone',         placeholder: 'e.g. (410) 555-0123',   type: 'text',  full: false },
      ],
    },
    {
      title: 'Company Address',
      fields: [
        { label: 'Street Address', key: 'address', placeholder: 'e.g. 123 Defense Blvd', type: 'text', full: true  },
        { label: 'City',           key: 'city',    placeholder: 'e.g. Baltimore',         type: 'text', full: false },
        { label: 'State',          key: 'state',   placeholder: 'e.g. MD',                type: 'text', full: false },
        { label: 'ZIP Code',       key: 'zip',     placeholder: 'e.g. 21201',             type: 'text', full: false },
      ],
    },
  ];

  return (
    <div style={{ padding: 28, overflowY: 'auto', height: '100%' }}>

      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-start', marginBottom: 24,
      }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>
            Company Profile
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>
            This information appears in your SSP and compliance reports
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {saved && (
            <span style={{
              fontSize: 12, color: 'var(--green)',
              background: 'var(--green-bg)', border: '1px solid var(--green)',
              padding: '5px 12px', borderRadius: 6,
            }}>
              Profile saved
            </span>
          )}
          <button
            onClick={handleSave}
            style={{
              fontSize: 12, fontWeight: 600, padding: '8px 18px', borderRadius: 6,
              cursor: 'pointer', border: '1px solid var(--accent)',
              background: 'var(--accent)', color: '#fff', fontFamily: 'var(--font)',
            }}
          >
            Save Profile
          </button>
        </div>
      </div>

      {/* Avatar Row */}
      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 10, padding: 20, marginBottom: 20,
        display: 'flex', alignItems: 'center', gap: 16,
      }}>
        <div style={{
          width: 56, height: 56, borderRadius: '50%', flexShrink: 0,
          background: 'linear-gradient(135deg, #2D7DD2, #0D3F73)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 20, fontWeight: 700, color: '#fff',
        }}>
          {(form.company_name || 'C').charAt(0).toUpperCase()}
        </div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>
            {form.company_name || 'Your Company Name'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
            {session?.user?.email}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            {form.cage_code && (
              <span style={{
                fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--accent)',
                background: 'var(--blue-bg)', padding: '2px 8px', borderRadius: 4,
                border: '1px solid rgba(45,125,210,0.2)',
              }}>
                CAGE: {form.cage_code}
              </span>
            )}
            {form.employees && (
              <span style={{
                fontSize: 10, color: 'var(--text3)',
                background: 'var(--surface)', padding: '2px 8px', borderRadius: 4,
                border: '1px solid var(--border)',
              }}>
                {form.employees} employees
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Form Sections */}
      {sections.map(section => (
        <div key={section.title} style={{
          background: 'var(--bg2)', border: '1px solid var(--border)',
          borderRadius: 10, padding: 20, marginBottom: 16,
        }}>
          <div style={{
            fontSize: 13, fontWeight: 600, color: 'var(--text)',
            marginBottom: 16, paddingBottom: 10,
            borderBottom: '1px solid var(--border)',
          }}>
            {section.title}
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 14,
          }}>
            {section.fields.map(field => (
              <div
                key={field.key}
                style={{ gridColumn: field.full ? '1 / -1' : 'auto' }}
              >
                <label style={labelStyle}>{field.label}</label>
                <input
                  type={field.type}
                  placeholder={field.placeholder}
                  value={form[field.key]}
                  onChange={e => handleChange(field.key, e.target.value)}
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = 'var(--accent)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border2)'}
                />
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Account Info */}
      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: 10, padding: 20, marginBottom: 16,
      }}>
        <div style={{
          fontSize: 13, fontWeight: 600, color: 'var(--text)',
          marginBottom: 16, paddingBottom: 10,
          borderBottom: '1px solid var(--border)',
        }}>
          Account Information
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          {[
            { label: 'Account Email',   value: session?.user?.email || 'Not set' },
            { label: 'Account Created', value: session?.user?.created_at
                ? new Date(session.user.created_at).toLocaleDateString()
                : 'Unknown'
            },
            { label: 'User ID',         value: session?.user?.id
                ? session.user.id.substring(0, 16) + '...'
                : 'Unknown'
            },
            { label: 'Current Plan',    value: profile?.is_trial ? 'Free Trial' : profile?.plan ? (profile.plan.charAt(0).toUpperCase() + profile.plan.slice(1)) : 'No active plan' },
          ].map(item => (
            <div key={item.label}>
              <label style={labelStyle}>{item.label}</label>
              <div style={{
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 6, padding: '9px 12px', fontSize: 13,
                color: 'var(--text2)',
                fontFamily: item.label === 'User ID' ? 'var(--mono)' : 'var(--font)',
              }}>
                {item.value}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Danger Zone */}
      <div style={{
        background: 'var(--bg2)', border: '1px solid var(--red)',
        borderRadius: 10, padding: 20,
      }}>
        <div style={{
          fontSize: 13, fontWeight: 600,
          color: 'var(--red)', marginBottom: 10,
        }}>
          Danger Zone
        </div>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text)' }}>
              Delete Account
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
              Permanently delete your account and all compliance data
            </div>
          </div>
          <button style={{
            fontSize: 12, fontWeight: 500, padding: '7px 14px', borderRadius: 6,
            cursor: 'pointer', border: '1px solid var(--red)',
            background: 'transparent', color: 'var(--red)', fontFamily: 'var(--font)',
            transition: 'all 0.15s',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = 'var(--red)';
            e.currentTarget.style.color = '#fff';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = 'var(--red)';
          }}
          >
            Delete Account
          </button>
        </div>
      </div>

    </div>
  );
}